import { SpaceDashboard } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import { useState } from 'react';
import { IndexPageRedirect } from '@/components/IndexPageRedirect';
import { SectionHeading } from '@/components/PartFinder/SectionHeading';
import { useGetAssetStateCounts } from '@/hooks/api/assets/useGetAssetStateCounts';
import { useDashboardLiveRefresh } from '@/hooks/api/tenants/useDashboardLiveRefresh';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { usePageTitle } from '@/hooks/usePageTitle';
import { isValidAssetState } from '@/utils/assetStateDisplayText';
import { ActivityTile } from './ActivityTile';
import { DashboardAutoRefreshToggle } from './DashboardAutoRefreshToggle';
import { AssetsTile } from './AssetsTile';
import { CoverageTile } from './CoverageTile';
import { IngestionStatusTile } from './IngestionStatusTile';
import { PartFinderTile } from './PartFinderTile';
import { RecommendationTile } from './RecommendationTile';
import { RecentFailuresTile } from './RecentFailuresTile';
import { ReportsTile } from './ReportsTile';
import { SystemHealthTile } from './SystemHealthTile';
import { useRecommendations } from './useRecommendations';

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  padding: '24px',
  overflowY: 'auto',
});

const Content = styled(Box)({
  width: '100%',
  maxWidth: '900px',
});

const TilesRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  // Stretch, not flex-start: side by side cards should share a height rather than each
  // collapsing to its own content, which looks broken when one of them is empty.
  alignItems: 'stretch',
  marginTop: '16px',
});

const TilesColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: 1,
  minWidth: '260px',
});

const RecommendationsRow = ({ assetsTotal }: { assetsTotal: number }) => {
  const { recommendations, isLoading } = useRecommendations({ assetsTotal });

  if (isLoading || recommendations.length === 0) {
    return;
  }

  return (
    <TilesRow>
      <TilesColumn>
        <RecommendationTile recommendations={recommendations} />
      </TilesColumn>
    </TilesRow>
  );
};

export const DashboardPage = () => {
  usePageTitle('Dashboard');
  const [autoRefresh, setAutoRefresh] = useState(true);
  useDashboardLiveRefresh({ enabled: autoRefresh });
  const { isPartFinderEnabled } = useFeatureFlags();
  const { data: currentUser, currentTenant, currentUserIsAtleastTenantViewer, permissions } = useGetCurrentUser();
  const { data: assetStateCounts, isLoading, isError } = useGetAssetStateCounts({ pollWhileIndexing: autoRefresh });

  if (!currentUserIsAtleastTenantViewer(currentTenant?.tenantId)) {
    return <IndexPageRedirect />;
  }

  const isIndexing = (assetStateCounts?.indexing ?? 0) > 0;

  const assetsTotal = Object.entries(assetStateCounts || {})
    .filter(([key]) => isValidAssetState(key))
    .reduce((sum, [, count]) => sum + count, 0);

  return (
    <PageRoot>
      <Content>
        <HeaderRow>
          <SectionHeading variant='page' icon={<SpaceDashboard />} title='Dashboard' />
          <DashboardAutoRefreshToggle enabled={autoRefresh} onChange={setAutoRefresh} />
        </HeaderRow>
        {currentUser?.user.isInternal && (
          <TilesRow>
            <TilesColumn>
              <SystemHealthTile />
            </TilesColumn>
          </TilesRow>
        )}
        {!isLoading && <RecommendationsRow assetsTotal={assetsTotal} />}
        <TilesRow>
          <IngestionStatusTile
            assetStateCounts={assetStateCounts}
            assetsTotal={assetsTotal}
            isLoading={isLoading}
            isError={isError}
          />
          <TilesColumn>
            <AssetsTile assetsUsed={assetsTotal} isLoading={isLoading} pollWhileIndexing={isIndexing} />
            <CoverageTile />
          </TilesColumn>
        </TilesRow>
        {permissions.canViewTenantActivity && (
          <TilesRow>
            <TilesColumn>
              <ActivityTile />
            </TilesColumn>
          </TilesRow>
        )}
        {isPartFinderEnabled && (
          <TilesRow>
            <TilesColumn>
              <PartFinderTile />
            </TilesColumn>
          </TilesRow>
        )}
        <TilesRow>
          <TilesColumn>
            <ReportsTile />
          </TilesColumn>
        </TilesRow>
        <TilesRow>
          <TilesColumn>
            <RecentFailuresTile />
          </TilesColumn>
        </TilesRow>
      </Content>
    </PageRoot>
  );
};
